// src/components/AdminTabUsers.js
import React, { useEffect, useMemo, useState } from "react";
import TabTwoColumnLayout from "../layouts/TabTwoColumnLayout";
import { apiFetch, getUmatByEmail } from "../utils/wkCommon";
import { getJwt } from "../utils/auth";
import AdminListItem from "./AdminListItem"; // sesuaikan path jika perlu
import LookupUmat from "./LookupUmat"; // komponen lookup yang Anda sediakan

// ============ STYLES (inline CSS in JS) ============
const styles = {
  formGroup: { marginBottom: "0.75rem" },
  label: { display: "block", marginBottom: "0.25rem", fontSize: "0.9rem", color: "#374151" },
  input: {
    padding: "0.45rem 0.8rem",
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    fontSize: "1rem",
    boxSizing: "border-box",
  },
  select: {
    padding: "0.45rem 0.8rem",
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    fontSize: "1rem",
    boxSizing: "border-box",
    background: "#fff",
  },
  button: {
    padding: "0.45rem 0.9rem",
    borderRadius: "0.375rem",
    border: "none",
    cursor: "pointer",
    background: "#2563eb",
    color: "#fff",
    fontSize: "0.95rem",
  },
  secondaryButton: {
    padding: "0.45rem 0.9rem",
    borderRadius: "0.375rem",
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
    fontSize: "0.95rem",
  },
  smallMuted: { fontSize: "0.85rem", color: "#6b7280" },
  readonlyChip: {
    display: "inline-block",
    padding: "0.4rem 0.6rem",
    borderRadius: "999px",
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
};

// ROLE ID mapping (fixed per instruksi)
const ROLE_ID_MAP = {
  Authenticated: 1,
  Member: 3,
  SysAdmin: 4,
};
const STRAPI_ROLES = Object.keys(ROLE_ID_MAP);

// Helper internal (defensive access to v5 flattened OR v4 attributes)
function docOf(item) {
  // if the response item is { data: { ... } } or already doc
  return item && item.data ? item.data : item;
}
function getDocumentId(item) {
  const d = docOf(item);
  return d?.documentId ?? d?.id ?? null;
}
function getField(item, field) {
  const d = docOf(item);
  if (!d) return undefined;
  if (field in d) return d[field];
  if (d.attributes && field in d.attributes) return d.attributes[field];
  if (d.data && d.data.attributes && field in d.data.attributes) return d.data.attributes[field];
  return undefined;
}
function getRelationDoc(item, relName) {
  const d = docOf(item);
  if (!d) return null;
  const rel = d[relName] ?? (d.attributes && d.attributes[relName]) ?? (d.data && d.data[relName]);
  if (!rel) return null;
  return rel.data ? rel.data : rel;
}
function getRelationName(item, relName) {
  const r = getRelationDoc(item, relName);
  if (!r) return null;
  return r?.name ?? r?.attributes?.name ?? (r?.attributes && r.attributes.name) ?? null;
}
// build populate query inline (no helper file)
function buildPopulate(arr = []) {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr.map((name, i) => `populate[${i}]=${encodeURIComponent(name)}`).join("&");
}

export default function AdminTabUsers() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]); // array of docs (v5/v4 tolerant)
  const [umatRoles, setUmatRoles] = useState([]);
  const [umats, setUmats] = useState([]);
  const [umatsByEmail, setUmatsByEmail] = useState({});
  const [selected, setSelected] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  // controls
  const [q, setQ] = useState("");
  const [filterStrapiRole, setFilterStrapiRole] = useState("");
  const [filterUmatRole, setFilterUmatRole] = useState("");
  const [filterLinked, setFilterLinked] = useState("");

  // form (now includes provider)
  const [form, setForm] = useState({
    email: "",
    username: "",
    provider: "google",
    strapiRole: "Authenticated",
    umatDocumentId: null,
    umatRoleIds: [],
  });

  const [lookupUmatValue, setLookupUmatValue] = useState(null);
  // lookupLocked can be derived but use state to control UI immediate
  const lookupLocked = !!form.umatDocumentId; // true if currently linked

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  async function loadAllData() {
    const usersQ = buildPopulate(["role", "umat"]);
    const usersResp = await apiFetch(`/api/users?${usersQ}&pagination[pageSize]=1000`);

    const rolesResp = await apiFetch(`/api/umat-roles?populate=umats&sort=name:asc`);

    const umatsList = [];
    let page = 1;
    const pageSize = 100;
    while (true) {
      const resp = await apiFetch(`/api/umats?filters%5BLoginEmail%5D%5B$notNull%5D=true&populate=umat_roles&pagination[page]=${page}&pagination[pageSize]=${pageSize}`);
      const items = Array.isArray(resp?.data) ? resp.data : resp?.data ? [resp.data] : [];
      if (items.length === 0) break;
      umatsList.push(...items);
      if (items.length < pageSize) break;
      page++;
    }

    const byEmail = {};
    umatsList.forEach(u => {
      const doc = docOf(u);
      const raw = doc?.LoginEmail ?? doc?.attributes?.LoginEmail ?? "";
      const email = raw.trim().toLowerCase();
      if (email) byEmail[email] = u;
    });

    return {
      users: normalizeToArray(usersResp),
      umatRoles: normalizeToArray(rolesResp),
      umats: umatsList,
      umatsByEmail: byEmail,
    };
  }

  // normalize response to array (v5/v4 tolerant)
  function normalizeToArray(resp) {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp.data)) return resp.data;
    if (resp.data && typeof resp.data === "object") return [resp.data];
    return [];
  }

  // extract umat role ids from agam-like doc (defensive)
  function extractUmatRoleIds(umatDoc) {
    if (!umatDoc) return [];
    const r = umatDoc?.umat_roles ?? umatDoc?.attributes?.umat_roles ?? umatDoc?.umat_roles?.data ?? umatDoc?.attributes?.umat_roles?.data ?? [];
    const arr = Array.isArray(r) ? r : (r?.data ?? []);
    return arr.map(x => x?.id ?? x?.documentId ?? x).filter(Boolean);
  }

  // initial load: users + umatRoles + umats (preload)
  {/*
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        // populate role & umat minimally for list
        const usersQ = buildPopulate(["role", "umat"]);
        // get users (we set large pageSize but still page safely if needed)
        const uResp = await apiFetch(`/api/users?${usersQ}&pagination[pageSize]=1000`);
        const urResp = await apiFetch(`/api/umat-roles?populate=umats&sort=name:asc&pagination[pageSize]=1000`);

        // fetch ALL umats but only those with a non-empty LoginEmail, paging safely
        const umatsPageSize = 100; // safe max per page
        let page = 1;
        let allUmats = [];
        const filterLoginNotNull = "filters%5BLoginEmail%5D%5B$notNull%5D=true"; // filters[LoginEmail][$notNull]=true

        while (true) {
          const url = `/api/umats?${filterLoginNotNull}&populate=umat_roles&pagination[page]=${page}&pagination[pageSize]=${umatsPageSize}`;
          const resp = await apiFetch(url);
          const pageItems = (Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []));
          if (!pageItems || pageItems.length === 0) break;
          allUmats.push(...pageItems);
          if (pageItems.length < umatsPageSize) break;
          page += 1;
        }

        // build byEmail map – only include LoginEmail that has a non-empty trimmed value
        const byEmail = {};
        allUmats.forEach((it) => {
          const doc = docOf(it);
          const raw =
            (doc?.LoginEmail) ??
            (doc?.attributes?.LoginEmail) ??
            (doc?.email) ??
            (doc?.attributes?.email) ??
            "";
          const email = String(raw || "").trim().toLowerCase();
          if (email.length > 0) {
            byEmail[email] = it;
          }
        });

        if (!mounted) return;

        const uList = normalizeToArray(uResp);
        const urList = normalizeToArray(urResp);

        setUsers(uList);
        setUmatRoles(urList);
        setUmats(allUmats);
        setUmatsByEmail(byEmail);
      } catch (e) {
        console.error("initial load error", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, []);
  */}

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await loadAllData();
      setUsers(data.users);
      setUmatRoles(data.umatRoles);
      setUmats(data.umats);
      setUmatsByEmail(data.umatsByEmail);
      setLoading(false);
    })();
  }, []);

  // when selecting a user: fetch full detail (role, umat.umat_roles) using documentId if available
  useEffect(() => {
    let mounted = true;
    if (!selected) {
      console.log("!selected",!selected)
      if (!isCreating) {
      console.log("!isCreating",!isCreating)
        setForm({
          email: "",
          username: "",
          provider: "google",
          strapiRole: "Authenticated",
          umatDocumentId: null,
          umatRoleIds: [],
        });
        setLookupUmatValue(null);
      }
      return;
    } 

    // update local state: clear linking
    setFormField("umatDocumentId", null);
    setFormField("umatRoleIds", []);
    setLookupUmatValue(null);

    (async () => {
      try {
        // determine identifier (documentId preferred)
        const base = docOf(selected);
        const docId = base?.documentId ?? base?.id;
        const q = buildPopulate(["role"]);
        const url = `/api/users?filters%5Bemail%5D%5B$eq%5D=${encodeURIComponent(base?.email)}&${q}`;
        const detail = await apiFetch(url);
        if (!mounted) return;

        let uDetail = null;
        if(detail && detail.length == 1) uDetail = detail[0];

        const email = uDetail?.email;
        const username = uDetail?.username;
        const provider = uDetail?.provider

        // role name - try populated role relation or fallback to role id map
        let roleName = null;
        if (uDetail?.role) {
          // v5/v4: role.data may include name or attributes.name
          roleName = uDetail?.role.name ?? null;
        }
        roleName = roleName || "Authenticated";

        // umat relation / link detection
        let umatDoc = null; if(email) umatDoc = await getUmatByEmail(email);
        let umatId = umatDoc?.id;
        let umatRoleIds = extractUmatRoleIds(umatDoc);

        setForm({
          email,
          username,
          provider,
          strapiRole: STRAPI_ROLES.includes(roleName) ? roleName : "Authenticated",
          umatDocumentId: umatId,
          umatRoleIds,
        });

        if (umatDoc) {
          // umatDoc mungkin v5 doc (flattened) atau v4 { id, attributes }
          const baseUmat = umatDoc;
          const normalizedUmat = {
            id: umatId,
            documentId: baseUmat.documentId,
            chineseName: baseUmat.chineseName ,
            fullName: baseUmat.fullName,
            umat_roles: baseUmat.umat_roles,
            // jika attributes ada, spread attributes; jika fields sudah flatten, spread baseUmat
            //...baseUmat
          };
          setLookupUmatValue(normalizedUmat);
        } else {
          setLookupUmatValue(null);
        } 

        // set selected to full detail doc (so selected has latest populated data)
        //setSelected(detail);
      } catch (e) {
        console.error("load user detail", e);
      }
    })();

    return () => (mounted = false);
  }, [selected?.id]);

  // filtered list (Strapi v5 aware)
  const filtered = useMemo(() => {
    const qLow = (q || "").trim().toLowerCase();
    return users.filter((u) => {
      const d = docOf(u);
      const email = (d?.email ?? d?.attributes?.email ?? "").toString().toLowerCase();
      const username = (d?.username ?? d?.attributes?.username ?? "").toString().toLowerCase();

      if (qLow && !email.includes(qLow) && !username.includes(qLow)) return false;

      // strapi role filter
      if (filterStrapiRole) {
        let roleName = null;
        if (d?.role?.data) roleName = d.role.data.name ?? d.role.data.attributes?.name;
        else roleName = d.role?.name ?? d.role?.attributes?.name ?? null;
        if (!roleName) {
          const roleId = d?.roleId ?? d?.role;
          const found = Object.entries(ROLE_ID_MAP).find(([, id]) => id === roleId);
          roleName = found ? found[0] : null;
        }
        if (!roleName || roleName !== filterStrapiRole) return false;
      }

      // umat-role filter: find umat (populated or via map)
      const umatDoc = d?.umat?.data ? d.umat.data : (umatsByEmail[email] ? docOf(umatsByEmail[email]) : null);
      if (filterUmatRole) {
        if (!umatDoc) return false;
        const list = (umatDoc?.umat_roles ?? umatDoc?.attributes?.umat_roles ?? umatDoc?.umat_roles?.data ?? umatDoc?.attributes?.umat_roles?.data) || [];
        const arr = Array.isArray(list) ? list : (list?.data ?? []);
        const matched = arr.some(rr => (rr?.name ?? rr?.attributes?.name ?? rr));
        if (!matched) return false;
      }

      // linked filter
      if (filterLinked === "linked") {
        const has = !!(d?.umat?.data || umatsByEmail[email]);
        if (!has) return false;
      }
      if (filterLinked === "not-linked") {
        const has = !!(d?.umat?.data || umatsByEmail[email]);
        if (has) return false;
      }

      return true;
    });
  }, [users, q, filterStrapiRole, filterUmatRole, filterLinked, umatsByEmail]);

  // left controls (sticky)
  const leftControls = (
    <div style={{ position: "sticky", top: 0, background: "#fff", zIndex: 20, padding: "0.5rem 0", borderBottom: "1px solid #eef2f7" }}>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search username or email..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1 }}
        />

        <select value={filterStrapiRole} onChange={(e) => setFilterStrapiRole(e.target.value)}>
          <option value="">All Strapi Roles</option>
          {STRAPI_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <select value={filterUmatRole} onChange={(e) => setFilterUmatRole(e.target.value)}>
          <option value="">All Umat Roles</option>
          {(Array.isArray(umatRoles) ? umatRoles : []).map(r => (
            <option key={r.id} value={r.attributes?.name ?? r.name}>{r.attributes?.name ?? r.name}</option>
          ))}
        </select>

        <select value={filterLinked} onChange={(e) => setFilterLinked(e.target.value)}>
          <option value="">All</option>
          <option value="linked">Linked</option>
          <option value="not-linked">Not linked</option>
        </select>

        {/* User creation disabled */}
        {/* <button
          onClick={() => {
            setIsCreating(true);
            setSelected(null);
            setForm({ email: "", username: "", provider: "google", strapiRole: "Authenticated", umatDocumentId: null, umatRoleIds: [] });
            setLookupUmatValue(null);
          }}
        > 
          User Baru
        </button>*/}
      </div>
    </div>
  );

  // left list
  const leftList = (
    <div>
      {loading ? <div>Loading…</div> : null}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {filtered.map((u) => {
          const d = docOf(u);
          const email = (d?.email ?? d?.attributes?.email ?? "") || "";
          const username = (d?.username ?? d?.attributes?.username ?? "") || "";
          // role name
          let roleName = "";
          if (d?.role?.data) roleName = d.role.data.name ?? d.role.data.attributes?.name ?? "";
          else roleName = d.role?.name ?? d.role?.attributes?.name ?? "";
          // provider
          const provider = d?.provider ?? d?.attributes?.provider ?? (email.includes("@") ? "local" : "");
          // linked detection: prefer populated relation, fallback to umatsByEmail
          let linked = false;
          let linkedName = null;
          if (d?.umat?.data) {
            const umatDoc = d.umat.data;
            linked = true;
            linkedName = umatDoc?.name ?? umatDoc?.attributes?.chineseName ?? umatDoc?.attributes?.fullName ?? null;
          } else {
            const found = umatsByEmail[email.toLowerCase()];
            if (found) {
              linked = true;
              const fd = docOf(found);
              linkedName = fd?.chineseName ?? fd?.fullName ?? fd?.attributes?.chineseName ?? fd?.attributes?.fullName ?? null;
            }
          }

          const subtitle = `${email} • ${provider}${roleName ? ` • ${roleName}` : ""} • ${linked ? "linked" : "not linked"}${linkedName ? ` • ${linkedName}` : ""}`;

          return (
            <AdminListItem
              key={getDocumentId(u) ?? d?.id}
              title={username || email}
              subtitle={subtitle}
              active={(docOf(selected)?.documentId ?? docOf(selected)?.id) === (d?.documentId ?? d?.id)}
              onClick={() => {
                setSelected(u);
                setIsCreating(false);
              }}
            />
          );
        })}
      </ul>
    </div>
  );

  function setFormField(k, v) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  // create user
  async function handleCreateUser(e) {
    e?.preventDefault?.();
    setErrorMessage(null);
    if (!form.email || !form.username || !form.strapiRole) {
      setErrorMessage("Email, username, and Strapi Role wajib diisi.");
      return;
    }
    const roleId = ROLE_ID_MAP[form.strapiRole];
    if (!roleId) {
      setErrorMessage(`Role mapping not found for ${form.strapiRole}`);
      return;
    }

    try {
      setSaving(true);
      const body = {
        data: {
          email: form.email,
          username: form.username,
          provider: form.provider || "google",
          confirmed: true,
          role: roleId,
        },
      };
      const resp = await apiFetch(`/api/users`, { method: "POST", body });

      // update umat if linked
      if (form.umatDocumentId) {
        const umatBody = { data: { LoginEmail: form.email } };
        if (Array.isArray(form.umatRoleIds)) umatBody.data.umat_roles = form.umatRoleIds;
        await apiFetch(`/api/umats/${encodeURIComponent(form.umatDocumentId)}`, { method: "PUT", body: umatBody });
      }

      // refresh
      const uResp = await apiFetch(`/api/users?${buildPopulate(["role","umat"])}`);
      const umResp = await apiFetch(`/api/umats?populate=umat_roles&pagination[pageSize]=1000`);
      setUsers(normalizeToArray(uResp));
      const umList = normalizeToArray(umResp);
      const byEmail = {};
      umList.forEach(it => {
        const dd = docOf(it);
        const mail = (dd?.LoginEmail ?? dd?.email ?? dd?.attributes?.LoginEmail ?? dd?.attributes?.email ?? "").toString().toLowerCase();
        if (mail) byEmail[mail] = it;
      });
      setUmats(umList);
      setUmatsByEmail(byEmail);

      setIsCreating(false);
      setSelected(null);
    } catch (err) {
      console.error("create user", err);
      setErrorMessage(err?.message || "Error saat membuat user");
    } finally {
      setSaving(false);
    }
  }

  async function handleUnlinkUmat() {
    if (!form.umatDocumentId) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      // prefer numeric id if available on lookupUmatValue; fallback to documentId
      const umatIdForUrl = lookupUmatValue.documentId;

      // send PUT to clear LoginEmail and umat_roles
      const body = { data: { LoginEmail: null, umat_roles: [] } };
      await apiFetch(`/api/umats/${encodeURIComponent(umatIdForUrl)}`, { method: "PUT", body });

      // update local state: clear linking
      setFormField("umatDocumentId", null);
      setFormField("umatRoleIds", []);
      setLookupUmatValue(null);

      // refresh umats map (re-run the data loader or remove entry from umatsByEmail)
      // minimal approach: remove entry from umatsByEmail for the email
      const byEmailCopy = { ...umatsByEmail };
      const lk = (form.email || "").toString().trim().toLowerCase();
      if (lk && byEmailCopy[lk]) delete byEmailCopy[lk];
      setUmatsByEmail(byEmailCopy);

      // optionally, refresh users list so UI reflects unlink
      const uResp = await apiFetch(`/api/users?${buildPopulate(["role","umat"])}`);
      setUsers(normalizeToArray(uResp));
    } catch (err) {
      console.error("unlink umat error", err);
      setErrorMessage("Gagal unlink Umat. Cek console/server log.");
    } finally {
      setSaving(false);
    }
  }

  // update user
  async function handleUpdateUser(e) {
    e?.preventDefault?.();
    if (!selected) return;
    setErrorMessage(null);

    try {
      setSaving(true);
      const selDoc = docOf(selected);

      //const updateBody = { data: { username: form.username, role: roleId, provider: form.provider } };
      //const updateBody = { data: { username: form.username, role: roleId, provider: form.provider } };
      //await apiFetch(`/api/users/${encodeURIComponent(selDoc?.id)}`, { method: "PUT", body: updateBody });
      if (form.umatDocumentId) {
        const umatBody = { data: { LoginEmail: form.email } };
        if (Array.isArray(form.umatRoleIds)) umatBody.data.umat_roles = form.umatRoleIds;
        await apiFetch(`/api/umats/${encodeURIComponent(form.umatDocumentId)}`, { method: "PUT", body: umatBody });
      }

      const data = await loadAllData();
      setUsers(data.users);
      setUmats(data.umats);
      setUmatsByEmail(data.umatsByEmail);

      // reselect updated user detail
      const detail = await apiFetch(`/api/users/${encodeURIComponent(selDoc?.id)}?${buildPopulate(["role"])}`);
      setSelected(detail);
    } catch (err) {
      console.error("update user", err);
      setErrorMessage(err?.message || "Error saat update user");
    } finally {
      setSaving(false);
    }
  }

  // Right panel
  const rightForm = (!selected && !isCreating) ? (
    <div style={{ color: "#6b7280" }}>
      Silakan pilih user di kiri atau klik <strong>User Baru</strong> untuk menambah user.
    </div>
  ) : (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>{isCreating ? "Tambah User Baru" : (getField(selected, "username") || "User")}</h3>

      <form onSubmit={isCreating ? handleCreateUser : handleUpdateUser}>
        <div style={{ marginBottom: "0.6rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => {
              const val = e.target.value;
              setFormField("email", val);
              if (isCreating) setFormField("username", val ? val.split("@")[0] : "");
            }}
            //required
            //readOnly={!isCreating}
            readOnly={true}
          />
        </div>

        <div style={{ marginBottom: "0.6rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Username</label>
          <input type="text" value={form.username} 
              //onChange={(e) => setFormField("username", e.target.value)} 
              readOnly={true} />
        </div>

        <div style={{ marginBottom: "0.6rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Provider</label>
          <select value={form.provider} 
            //</div>onChange={(e) => setFormField("provider", e.target.value)} 
            disabled={true}>
            <option value="google">google</option>
            <option value="local">local</option>
          </select>
        </div>

        <div style={{ marginBottom: "0.6rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Strapi Role</label>
          <select value={form.strapiRole} 
            //onChange={(e) => setFormField("strapiRole", e.target.value)} 
            disabled={true}>
            {STRAPI_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: "0.6rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Link ke Umat (lookup)</label>
          {lookupLocked ? (
            // locked view: show chip + Unlink button
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <div style={styles.readonlyChip}>
                {lookupUmatValue ? (lookupUmatValue.chineseName || lookupUmatValue.fullName || `Umat #${lookupUmatValue.id}`) : (form.email || "Linked")}
              </div>

              <button
                type="button"
                style={styles.secondaryButton}
                onClick={async () => {
                  // call unlink handler (see function below)
                  await handleUnlinkUmat();
                }}
              >
                Unlink
              </button>
            </div>
          ) : (
            // editable lookup when not linked
            <LookupUmat
              value={lookupUmatValue}
              onChange={(item) => {
                // item is normalized object from LookupBase / LookupUmat
                if (!item) {
                  setLookupUmatValue(null);
                  setFormField("umatDocumentId", null);
                  setFormField("umatRoleIds", []);
                  return;
                }
                setLookupUmatValue(item);
                const docId = item.documentId ?? item.id;
                setFormField("umatDocumentId", docId);
                // extract roles
                const roles = item.umat_roles ?? item.attributes?.umat_roles ?? item.umat_roles?.data ?? item.attributes?.umat_roles?.data ?? [];
                const arr = Array.isArray(roles) ? roles : (roles?.data ?? []);
                const ids = arr.map(x => x?.id ?? x?.documentId ?? x).filter(Boolean);
                setFormField("umatRoleIds", ids);
              }}
            />
          )}
        </div>

        <div style={{ marginBottom: "0.6rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Umat-Roles (pilih 0..n)</label>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {(Array.isArray(umatRoles) ? umatRoles : []).map(r => {
              const id = r.id ?? r.documentId;
              const name = r.attributes?.name ?? r.name;
              const checked = Array.isArray(form.umatRoleIds) && form.umatRoleIds.includes(id);
              return (
                <label key={id} style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const checkedNow = e.target.checked;
                      setForm(prev => {
                        const cur = Array.isArray(prev.umatRoleIds) ? [...prev.umatRoleIds] : [];
                        if (checkedNow) {
                          if (!cur.includes(id)) cur.push(id);
                        } else {
                          const idx = cur.indexOf(id);
                          if (idx >= 0) cur.splice(idx, 1);
                        }
                        return { ...prev, umatRoleIds: cur };
                      });
                    }}
                  />
                  {name}
                </label>
              );
            })}
          </div>
        </div>

        {errorMessage ? <div style={{ color: "red", marginBottom: "0.5rem" }}>{errorMessage}</div> : null}

        <div style={{ marginTop: "0.75rem" }}>
          <button type="submit" disabled={saving} style={{ padding: "0.45rem 0.8rem" }}>
            {saving ? "Saving…" : (isCreating ? "Create User" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );

  return <TabTwoColumnLayout leftControls={leftControls} leftList={leftList} rightForm={rightForm} />;
}
